/**
 * The screen-reader half of a sortable list, in one place.
 *
 * Eleven blocks hand-built nineteen of these `accessibility={{ ... }}` objects — every one with
 * the same four hooks, the same index-of-active/over arithmetic, and the same four strings, keyed
 * per block. Four of the five sentences were byte-identical across all of them; the only part that
 * genuinely varies is the noun in "Picked up ___", because that names what is being dragged.
 *
 * These strings are the ones nobody notices drifting: they are only ever heard, never seen. Two
 * blocks announcing a reorder differently is invisible in review and obvious to someone using a
 * screen reader, which is exactly the kind of divergence worth making impossible rather than
 * merely discouraged.
 */
import type { Announcements, ScreenReaderInstructions } from '@dnd-kit/core';

/** What the list holds, for the "Picked up ___" announcement. */
export type DndNoun = 'feedback' | 'option' | 'keyword' | 'word' | 'item';

const PICKED_UP: Record<DndNoun, string> = {
  feedback: 'blocks.dnd.pickedUpFeedback',
  option:   'blocks.dnd.pickedUpOption',
  keyword:  'blocks.dnd.pickedUpKeyword',
  word:     'blocks.dnd.pickedUpWord',
  item:     'blocks.dnd.pickedUpItem',
};

/**
 * `ids` is the list in its CURRENT rendered order, which is what a position announcement has to be
 * counted against — pass `items.map(i => i.id)`, or the id array directly where a block already
 * keeps one (Ordering's `reconciled`).
 */
export function dndA11y(
  t: (key: string, opts?: Record<string, unknown>) => string,
  ids: string[],
  noun: DndNoun,
): { screenReaderInstructions: ScreenReaderInstructions; announcements: Announcements } {
  const at = (id: string | number) => ids.indexOf(String(id)) + 1;
  const total = ids.length;
  return {
    screenReaderInstructions: { draggable: t('blocks.dnd.instructions') },
    announcements: {
      onDragStart: ({ active }) => t(PICKED_UP[noun], { position: at(active.id), total }),
      onDragOver:  ({ over })   => (over ? t('blocks.dnd.over', { position: at(over.id), total }) : undefined),
      onDragEnd:   ({ over })   => (over ? t('blocks.dnd.dropped', { position: at(over.id), total }) : undefined),
      onDragCancel: () => t('blocks.dnd.cancelled'),
    },
  };
}
